<template>
  <div :class="$style.container">
    <SettingsPagePanel>
      <SettingsPagePanelHeader>
        <template #title>自動応答チャットメッセージ 条件の編集</template>
      </SettingsPagePanelHeader>
      <AnswerMessageAutoChatReplySettingTable
        v-if="form"
        :form="form"
        @save="saveSetting"
      />
      <div :class="$style.delete">
        <FlatButton
          variant="caution"
          size="medium"
          @click="deleteSetting"
          :disabled="deleting"
        >
          設定を削除
        </FlatButton>
      </div>
    </SettingsPagePanel>
    <SettingsPageAutoChatReplyKeywordLinkSectionPanel
      v-if="autoChatReplySetting"
      :auto-chat-reply-setting="autoChatReplySetting"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'

import { useAlert } from '@/libs/alertPortal'
import { captureException } from '@sentry/vue'

import FlatButton from '@/components/atoms/FlatButton.vue'
import AnswerMessageAutoChatReplySettingTable, {
  type AutoChatReplySettingTableForm,
} from './AnswerMessageAutoChatReplySettingTable.vue'

import { assertExhaustive } from '@/libs/utilities/assertExhaustive'
import {
  AutoChatReplySettingExtraType,
  ExtraBookingRequestAvailableTimeSlotRange,
} from '@/types/schema.generated'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import {
  type UpdateAutoChatReplySettingMutation,
  useDeleteAutoChatReplySettingMutation,
  useGetAutoChatReplySettingByIdQuery,
  useUpdateAutoChatReplySettingMutation,
} from './SettingsPageAutoChatReplyDetailView.generated'
import SettingsPageAutoChatReplyKeywordLinkSectionPanel from './SettingsPageAutoChatReplyKeywordLinkSectionPanel.vue'
import { useLogger } from '@/logger/logger'

const emit = defineEmits<{
  (
    event: 'onBeforeLeaveConfirmation',
    callbacks: { onConfirm: () => void; onAbort: () => void }
  ): void
}>()

const route = useRoute('autoChatReplySettingDetail')

const settingId = route.params.autoChatReplySettingId
const { result, error } = useGetAutoChatReplySettingByIdQuery({
  id: settingId,
})

const router = useRouter()
const alert = useAlert()

const isCompleted = ref(false)

const isEditing = computed({
  get(): boolean {
    return route.name === 'autoChatReplySettingDetail'
  },
  set(value: boolean): void {
    const name = value ? 'autoChatReplySettingDetail' : 'autoChatReplySetting'
    // eslint-disable-next-line fp/no-mutating-methods
    router.push({ name })
  },
})

onBeforeRouteLeave((to, from, next) => {
  if (from.name !== 'autoChatReplySettingDetail') {
    next()
    return
  }
  if (to.name === 'autoChatReplySetting' && !isCompleted.value) {
    next()
    return
  }
  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      isCompleted.value = true
      next()
    },
    onAbort: () => next(false),
  })
})

watch(error, e => {
  if (e) {
    if (
      e.graphQLErrors.some(
        e =>
          e.extensions?.code === 'NOT_FOUND' &&
          e.path?.[0] === 'autoChatReplySetting'
      )
    ) {
      router.replace({ name: 'errorNotFound' })
      return
    }

    captureException(e)
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
  }
})

const autoChatReplySetting = computed(() => {
  if (!result.value) {
    return undefined
  }

  const { autoChatReplySetting } = result.value
  return autoChatReplySetting
})

const form = computed<AutoChatReplySettingTableForm | undefined>(() => {
  if (!autoChatReplySetting.value) {
    return undefined
  }

  const extra: AutoChatReplySettingTableForm['extra'] = (() => {
    switch (autoChatReplySetting.value.extra.type) {
      case AutoChatReplySettingExtraType.None:
        return {
          type: AutoChatReplySettingExtraType.None,
        }
      case AutoChatReplySettingExtraType.Question:
        return {
          type: AutoChatReplySettingExtraType.Question,
          selections:
            autoChatReplySetting.value.extra.question?.selections.map(
              s => s.text
            ) ?? [],
        }
      case AutoChatReplySettingExtraType.BookingRequest:
        return {
          type: AutoChatReplySettingExtraType.BookingRequest,
          availableTimeSlotRange:
            autoChatReplySetting.value.extra.bookingRequest
              ?.availableTimeSlotRange ??
            ExtraBookingRequestAvailableTimeSlotRange.Anytime,
          fallbackUrl:
            autoChatReplySetting.value.extra.bookingRequest?.fallbackUrl ?? '',
          assignedCalendarGuid:
            autoChatReplySetting.value.extra.bookingRequest
              ?.assignedCalendarGuid ?? '',
        }
      default:
        assertExhaustive(autoChatReplySetting.value.extra.type)
    }
  })()

  return {
    ...autoChatReplySetting.value,
    extra,
  }
})

const logger = useLogger()

const { mutate: updateAutoChatReplySetting } =
  useUpdateAutoChatReplySettingMutation({})
const { mutate: deleteAutoChatReplySetting } =
  useDeleteAutoChatReplySettingMutation({})

const handleSetting = async (
  setting: Maybe<
    UpdateAutoChatReplySettingMutation['updateAutoChatReplySetting']
  >
) => {
  if (setting === undefined) {
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
    return
  }

  switch (setting.__typename) {
    case 'SaveAutoChatReplySettingSuccess':
      await logger.ltlPeople.event.updateAutoChatReplySetting(
        logger.buildMeta(),
        {
          data: {
            id: Number(settingId),
            keyword: setting.autoChatReplySetting.keyword,
            isEnabled: setting.autoChatReplySetting.isEnabled,
            body: setting.autoChatReplySetting.body,
            extra: setting.autoChatReplySetting.extra,
          },
        }
      )
      alert.success('自動応答メッセージを更新しました')
      return
    case 'AutoChatReplySettingDuplicateKeywordError':
      alert.error('自動応答する選択肢が他の設定と重複しています')
      return
    default:
      alert.error(
        'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
      )
      return
  }
}

const saveSetting = async (form: AutoChatReplySettingTableForm) => {
  try {
    const res = await updateAutoChatReplySetting({
      input: {
        id: settingId,
        keyword: form.keyword,
        isEnabled: form.isEnabled,
        body: form.body,
        extra: buildInputExtra(form),
      },
    })
    await handleSetting(res?.data?.updateAutoChatReplySetting)
    isEditing.value = false
  } catch (e) {
    captureException(e)
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
  }
}

const deleting = ref(false)

const deleteSetting = async () => {
  if (!autoChatReplySetting.value) {
    return
  }
  const logParams = Array({
    id: Number(settingId),
    isEnabled: autoChatReplySetting.value.isEnabled,
    keyword: autoChatReplySetting.value.keyword,
    body: autoChatReplySetting.value.body,
    extra: autoChatReplySetting.value.extra,
  })

  const confirmResult = window.confirm(
    '自動応答メッセージの設定を削除しますか？'
  )
  if (!confirmResult) {
    return
  }

  deleting.value = true

  try {
    await deleteAutoChatReplySetting(
      { id: settingId },
      {
        update(cache) {
          const normalizedId = cache.identify({
            id: settingId,
            __typename: 'AutoChatReplySetting',
          })
          cache.evict({ id: normalizedId })
          cache.gc()
          alert.success('自動応答メッセージを削除しました')
          // eslint-disable-next-line fp/no-mutating-methods
          router.push({
            name: 'autoChatReplySetting',
          })
        },
      }
    )
    logger.ltlPeople.event.deleteAutoChatReplySettings(logger.buildMeta(), {
      data: logParams,
    })
  } catch (e) {
    captureException(e)
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
  } finally {
    deleting.value = false
  }
}

const buildInputExtra = (form: AutoChatReplySettingTableForm) => {
  switch (form.extra.type) {
    case AutoChatReplySettingExtraType.None:
      return {
        type: AutoChatReplySettingExtraType.None,
      }
    case AutoChatReplySettingExtraType.Question:
      return {
        type: AutoChatReplySettingExtraType.Question,
        question: {
          selections: form.extra.selections,
        },
      }
    case AutoChatReplySettingExtraType.BookingRequest:
      return {
        type: AutoChatReplySettingExtraType.BookingRequest,
        bookingRequest: {
          availableTimeSlotRange: form.extra.availableTimeSlotRange,
          fallbackUrl:
            form.extra.fallbackUrl === '' ? null : form.extra.fallbackUrl,
          assignedCalendarGuid:
            form.extra.assignedCalendarGuid === ''
              ? null
              : form.extra.assignedCalendarGuid,
        },
      }
    default:
      return assertExhaustive(form.extra)
  }
}
</script>

<style lang="scss" module>
.container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.delete {
  text-align: center;
  margin-top: 40px;
}
</style>
